import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ExternalLink, RefreshCcw, ArrowLeft, Check, X } from "lucide-react";
import ScoreGauge from "../components/ScoreGauge";
import IngredientCard from "../components/IngredientCard";
import DNALoader from "../components/DNALoader";
import SearchBar from "../components/SearchBar";
import FavoriteButton from "../components/FavoriteButton";
import ShareMenu from "../components/ShareMenu";
import AdSlot from "../components/AdSlot";
import SEO, { canonicalFor, titleFor, SITE_NAME } from "../lib/seo";
import { HealthNotice } from "./StaticPages";
import { analyzeProduct, getProduct, displayName } from "../lib/api";
import { amazonLink } from "../lib/ads";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");

export default function ProductAnalysis() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const queryName = params.get("q");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cachedMeta, setCachedMeta] = useState(null);

  const run = async (force = false) => {
    setLoading(true);
    setErr(null);
    try {
      if (!force) {
        try {
          const doc = await getProduct(slug);
          setData(doc);
          setCachedMeta({ cached: true, lastAnalyzed: doc.lastAnalyzed });
          setLoading(false);
          return;
        } catch (_) { /* not found, fall through */ }
      }
      const name = queryName || slug.replace(/-/g, " ");
      const res = await analyzeProduct(name, force);
      setData(res.data);
      setCachedMeta({ cached: res.cached, lastAnalyzed: res.data.lastAnalyzed });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(false); /* eslint-disable-next-line */ }, [slug]);

  if (loading) {
    return (
      <div data-testid="analysis-loading" className="max-w-4xl mx-auto px-6 py-24">
        <DNALoader label={queryName ? `Analyzing "${queryName}"...` : "Loading analysis..."} />
        <div className="text-center text-sm text-[color:var(--text-3)] mt-4">
          First-time analysis may take 10–30 seconds as Claude inspects every ingredient.
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div data-testid="analysis-error" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <AlertTriangle className="h-10 w-10 text-[color:var(--danger)] mx-auto mb-4" />
        <h2 className="font-display text-2xl font-semibold mb-2">Analysis failed</h2>
        <p className="text-[color:var(--text-2)] mb-6">{err}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => run(false)} className="lime-btn rounded-lg px-4 py-2 text-sm" data-testid="retry-btn">
            Try again
          </button>
          <Link to="/" className="ghost-btn rounded-lg px-4 py-2 text-sm">Back home</Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const color = ratingColor(data.overallRating);
  const dname = displayName(data.productName);
  const nh = data.nutritionHighlights || {};
  const total = (nh.redIngredients || 0) + (nh.yellowIngredients || 0) + (nh.greenIngredients || 0) || data.ingredients?.length || 1;
  const redPct = ((nh.redIngredients || 0) / total) * 100;
  const yellowPct = ((nh.yellowIngredients || 0) / total) * 100;
  const greenPct = ((nh.greenIngredients || 0) / total) * 100;

  return (
    <div data-testid="analysis-page" className="relative max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <SEO
        title={titleFor(`${data.productName} Ingredients: ${data.overallScore}/100 Safety Score`)}
        description={data.overallVerdict || `Full ingredient safety breakdown for ${data.productName} — traffic-light ratings, warnings and healthier alternatives.`}
        canonical={canonicalFor(`/product/${slug}`)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: data.productName,
          brand: data.brand || undefined,
          category: data.category || undefined,
          description: data.overallVerdict || undefined,
          review: {
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: data.overallScore, bestRating: 100, worstRating: 0 },
            author: { "@type": "Organization", name: SITE_NAME },
          },
        }}
      />
      <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
        <Link to="/" data-testid="back-home" className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {cachedMeta?.cached && (
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-3)]">
              Cached · {new Date(cachedMeta.lastAnalyzed).toLocaleDateString()}
            </span>
          )}
          <FavoriteButton product={data} />
          <button onClick={() => run(true)} className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-2" data-testid="reanalyze-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Re-analyze
          </button>
          <ShareMenu data={{ ...data, slug }} />
        </div>
      </div>

      <div className="mb-8">
        <HealthNotice />
      </div>

      {/* HERO */}
      <section className="grid md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-center mb-12">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-3">
            {data.brand} {data.category && <>· {data.category}</>}
          </div>
          <h1 data-testid="product-title" title={data.productName} className={`font-display font-bold tracking-tighter leading-[1.04] ${dname.length > 24 ? "text-3xl md:text-5xl" : "text-4xl md:text-6xl"}`}>
            {dname}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[color:var(--text-2)] max-w-xl leading-relaxed">
            {data.overallVerdict}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            <DietBadge label="Vegan" ok={data.dietaryInfo?.vegan} />
            <DietBadge label="Vegetarian" ok={data.dietaryInfo?.vegetarian} />
            <DietBadge label="Gluten-Free" ok={data.dietaryInfo?.glutenFree} />
            <DietBadge label="Dairy-Free" ok={data.dietaryInfo?.dairyFree} />
            <DietBadge label="Nut-Free" ok={data.dietaryInfo?.nutFree} />
            <DietBadge label="Palm Oil-Free" ok={data.dietaryInfo?.palmoilFree} />
          </div>
        </div>
        <div className="flex justify-center md:justify-end">
          <ScoreGauge score={data.overallScore} rating={data.overallRating} size={240} />
        </div>
      </section>

      {/* NUTRITION STACKED BAR */}
      <section className="surface rounded-xl p-5 mb-8" data-testid="nutrition-bar">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)]">
            Ingredient Breakdown
          </div>
          <div className="font-mono text-xs text-[color:var(--text-2)]">
            {nh.redIngredients || 0} red · {nh.yellowIngredients || 0} amber · {nh.greenIngredients || 0} green
          </div>
        </div>
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-white/5">
          <div style={{ width: `${redPct}%`, background: "#FF3B3B" }} />
          <div style={{ width: `${yellowPct}%`, background: "#FFB800" }} />
          <div style={{ width: `${greenPct}%`, background: "#00C853" }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
          <Stat label="Calories" value={data.calories} />
          <Stat label="Serving" value={data.servingSize} />
          <Stat label="Sugar" value={nh.sugar} />
          <Stat label="Sat. Fat" value={nh.saturatedFat} />
        </div>
      </section>

      {/* WARNINGS */}
      {data.warnings?.length > 0 && (
        <section className="space-y-3 mb-10" data-testid="warnings-section">
          {data.warnings.map((w, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              className="shake-once surface rounded-xl p-4 flex items-start gap-3 border-l-4"
              style={{ borderLeftColor: "#FF3B3B", boxShadow: "0 0 28px rgba(255,59,59,0.15)" }}
              data-testid={`warning-${i}`}
            >
              <AlertTriangle className="h-5 w-5 text-[color:var(--danger)] flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div className="font-display font-semibold text-base md:text-lg text-[color:var(--text)] uppercase tracking-tight">
                {w}
              </div>
            </motion.div>
          ))}
        </section>
      )}

      {/* INGREDIENTS */}
      <section className="mb-12">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-1">
              Full Analysis
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold">
              Ingredients ({data.ingredients?.length || 0})
            </h2>
          </div>
        </div>
        <div className="space-y-3">
          {data.ingredients?.map((ing, i) => (
            <IngredientCard key={i} ingredient={ing} index={i} />
          ))}
        </div>
      </section>

      {/* ALTERNATIVES */}
      {data.healthierAlternatives?.length > 0 && (
        <section className="mb-12" data-testid="alternatives-section">
          <div className="mb-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-1">
              Upgrade Your Pantry
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold">Healthier alternatives</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {data.healthierAlternatives.map((alt, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="surface rounded-xl p-5 hover:border-[color:var(--lime)]/40 transition-colors"
                data-testid={`alternative-${i}`}
              >
                <div className="font-display font-semibold text-lg mb-2">{alt.name}</div>
                <p className="text-sm text-[color:var(--text-2)] mb-4 leading-relaxed">{alt.reason}</p>
                <a
                  href={amazonLink(alt.searchQuery || alt.name)}
                  target="_blank"
                  rel="noreferrer"
                  className="lime-btn rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1.5 font-mono uppercase tracking-wider"
                >
                  Find on Amazon <ExternalLink className="h-3 w-3" />
                </a>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <div className="mb-12">
        <AdSlot slot="product-mid" size="square" />
      </div>

      {/* FUN FACT */}
      {data.funFact && (
        <section className="surface-elev rounded-xl p-6 mb-12" data-testid="fun-fact">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--lime)] mb-2">
            Fun Fact
          </div>
          <p className="font-display text-lg md:text-xl leading-snug">{data.funFact}</p>
          {data.parentCompany && (
            <div className="mt-4 text-xs font-mono uppercase tracking-wider text-[color:var(--text-3)]">
              Made by {data.parentCompany}
            </div>
          )}
        </section>
      )}

      {/* SEARCH AGAIN */}
      <section className="surface rounded-xl p-6" data-testid="search-again">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-3">
          Check another product
        </div>
        <SearchBar size="md" />
      </section>
    </div>
  );
}

const DietBadge = ({ label, ok }) => (
  <span
    className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded border inline-flex items-center gap-1.5"
    style={{
      color: ok ? "#00C853" : "#FF3B3B",
      borderColor: ok ? "rgba(0,200,83,0.3)" : "rgba(255,59,59,0.3)",
      background: ok ? "rgba(0,200,83,0.05)" : "rgba(255,59,59,0.05)",
    }}
    data-testid={`diet-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    {label}
  </span>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[color:var(--text-3)] mb-1">{label}</div>
    <div className="font-display text-base text-[color:var(--text)] truncate">{value || "—"}</div>
  </div>
);
